import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Mail, Pencil, Send, UserPlus, X } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { sugerirNomeUsuario } from '@malgoos/shared';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, del, ErroRequisicao, get, getTodos, mensagemDeErro, post } from '../../nucleo/api';
import { email, relativo } from '../../nucleo/formato';
import type { PropsDialogoConvite } from '../../nucleo/modulos-web';
import { useSessao } from '../../nucleo/sessao';
import { usePapeis } from './MatrizPermissoes';
import { nomeDoPapel, type Convite, type Lista } from './tipos';
import { AcessoCriadoModal, type DadosAcessoCriado } from './AcessoCriado';
import { caminhoDaFicha, NomeComFicha } from './link-ficha';

/** Review r42 visual (B1): "Reenviar" só no convite que quem vê pode reenviar (o servidor manda `reenviavel`). */
export const podeReenviar = (c: Pick<Convite, 'reenviavel'>) => c.reenviavel !== false;

type TipoConvite = 'funcionario' | 'parceiro' | 'convidado';
const PAPEIS_PORTAL = ['dono', 'financeiro', 'dp', 'fiscal', 'societario', 'colaborador'] as const;

/**
 * Convite novo. Lote de 19/10 (item 16): o primeiro campo é o tipo. Funcionário: o já cadastrado em Pessoas sem acesso, ou um
 * novo só com nome, e-mail, departamento, função e empresa (CPF e admissão ficam a completar). Val1 (28/10): quem convida
 * escolhe o papel; a função só sugere (o papel dela vem preenchido, trocável). Cliente ou parceiro: o acesso do Portal (sem papel do sistema), desabilitado
 * com o motivo sem o Portal ou sem a permissão. Convidado: o convite de antes (e-mail, nome e papel; Dono só por promoção).
 * `funcionario` (ficha de Pessoas, "Convidar para o sistema"): abre já no tipo Funcionário com a pessoa escolhida.
 */
export function ConviteModal({ aberto, aoFechar, funcionario }: PropsDialogoConvite) {
  const { t } = useTranslation('admin');
  const { t: tp } = useTranslation('portal');
  const { t: tpe } = useTranslation('pessoas');
  const { t: tn, i18n } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  // Review rval1-11 visual (M2): o papel do convite de funcionário vem da leitura do Pessoas (`pessoas.convidar` desde o val5, só os papéis que
  // quem convida pode dar); a lista do Admin (`admin.ver`) só para o convidado, que já pede `admin.usuarios`.
  const podeConvidado = s.pode('admin.usuarios');
  const papeis = usePapeis(aberto && podeConvidado && s.pode('admin.ver'));
  const papeisFuncionario = useQuery({
    queryKey: ['/pessoas/funcionarios/papeis-convite'],
    queryFn: () => get<Lista<{ id: string; nome: string; codigo: string; de_fabrica: boolean }>>('/pessoas/funcionarios/papeis-convite'),
    enabled: aberto && s.instalado('pessoas') && s.pode('pessoas.convidar'),
  });
  const [valores, setValores] = useState<Record<string, unknown>>({});
  // val1 (28/10): o acesso por nome de usuário devolve a senha provisória uma vez (mostrada no modal próprio).
  const [criado, setCriado] = useState<DadosAcessoCriado | null>(null);
  // rval1-1 (ROB-B4): o id da conta sem e-mail nasce com o formulário; o duplo envio repete o id e não cria outra conta.
  const [idAcesso, setIdAcesso] = useState(() => crypto.randomUUID());
  const opcoes = (papeis.data?.itens ?? []).filter((p) => p.codigo !== 'dono').map((p) => ({ valor: p.id, rotulo: nomeDoPapel(t, p) }));
  const opcoesFuncionario = (papeisFuncionario.data?.itens ?? []).map((p) => ({ valor: p.id, rotulo: nomeDoPapel(t, p) }));
  const motivoPessoas = !s.instalado('pessoas') ? t('usuarios.convite.semPessoas') : !s.pode('pessoas.convidar') ? t('usuarios.convite.semPermissaoPessoas') : null;
  const motivoPortal = !s.instalado('portal') ? t('usuarios.convite.semPortal') : !s.pode('portal.acessos') ? t('usuarios.convite.semPermissaoPortal') : null;
  const tipos: { valor: TipoConvite; rotulo: string; desabilitado?: boolean }[] = [
    { valor: 'funcionario', rotulo: t('usuarios.convite.tipos.funcionario'), desabilitado: !!motivoPessoas },
    { valor: 'parceiro', rotulo: t('usuarios.convite.tipos.parceiro'), desabilitado: !!motivoPortal },
    { valor: 'convidado', rotulo: t('usuarios.convite.tipos.convidado'), desabilitado: !podeConvidado },
  ];
  const tipoInicial: TipoConvite = funcionario ? 'funcionario' : podeConvidado ? 'convidado' : !motivoPessoas ? 'funcionario' : 'parceiro';
  const tipo = (valores.tipo as TipoConvite | undefined) ?? tipoInicial;
  const motivoDoTipo = tipo === 'funcionario' ? motivoPessoas : tipo === 'parceiro' ? motivoPortal : null;
  const empresas = s.empresas?.opcoes ?? [];
  const ehFuncionario = (v: Record<string, unknown>) => (v.tipo ?? tipoInicial) === 'funcionario';
  const ehNovo = (v: Record<string, unknown>) => ehFuncionario(v) && v.origem === 'novo';
  const ehExistente = (v: Record<string, unknown>) => ehFuncionario(v) && v.origem !== 'novo';
  const ehParceiro = (v: Record<string, unknown>) => (v.tipo ?? tipoInicial) === 'parceiro';
  const ehConvidado = (v: Record<string, unknown>) => (v.tipo ?? tipoInicial) === 'convidado';
  // Val7 (29/10, ficha FUN_formas-de-acesso): duas formas (e-mail de login e nome de usuário), ao menos uma marcada. Havendo
  // e-mail, vai o convite com o link (e o nome de usuário, se marcado); sem e-mail e só o usuário, a senha provisória na tela.
  const comEmail = (v: Record<string, unknown>) => v.login_email !== false;
  const comUsuario = (v: Record<string, unknown>) => v.login_usuario !== false;
  const semEmailDigitado = (v: Record<string, unknown>) => !String(v.email ?? '').trim();
  const soUsuarioSemEmail = (v: Record<string, unknown>) => !ehParceiro(v) && !comEmail(v) && comUsuario(v) && semEmailDigitado(v);
  const nomeBase = String((ehConvidado(valores) ? valores.nome_convidado : ehNovo(valores) ? valores.nome : funcionario?.nome) ?? '').trim();
  const exemploUsuario = nomeBase ? sugerirNomeUsuario(nomeBase) : 'nome.sobrenome';
  // Val1 (28/10): a função sugere o papel. Ao escolher a função (novo) ou o funcionário (existente), o papel dela entra no campo
  // vazio (`ajuste` só preenche o que está vazio: o que a pessoa escolheu fica). Review rval1-11 visual (M1): só quando o papel
  // sugerido está na lista (a que quem convida pode dar) e depois que a lista chega; o padrão do convidado (Operador) também
  // entra pelo `ajuste`, porque a lista do Admin pode chegar depois de o formulário montar.
  const funcaoNova = ehNovo(valores) && typeof valores.funcao_id === 'string' ? valores.funcao_id : null;
  const doFuncionario = ehExistente(valores) && typeof valores.funcionario_id === 'string' ? valores.funcionario_id : (funcionario?.id ?? null);
  const sugestao = useQuery({
    queryKey: ['convite', 'papel-sugerido', funcaoNova, doFuncionario],
    enabled: aberto && s.pode('pessoas.ver') && !!(funcaoNova || doFuncionario),
    queryFn: async () => {
      const funcaoId = funcaoNova ?? (doFuncionario ? (await get<{ funcao_id: string | null }>(`/pessoas/funcionarios/${doFuncionario}`)).funcao_id : null);
      if (!funcaoId) return null;
      const funcoes = await getTodos<{ id: string; papel_id: string | null }>('/pessoas/funcoes');
      return funcoes.itens.find((f) => f.id === funcaoId)?.papel_id ?? null;
    },
  });
  const [ajuste, setAjuste] = useState<{ n: number; dados: Record<string, unknown> } | undefined>(undefined);
  const sugerido = sugestao.data && opcoesFuncionario.some((o) => o.valor === sugestao.data) ? sugestao.data : null;
  const padraoConvidado = (papeis.data?.itens ?? []).find((p) => p.codigo === 'operador')?.id ?? opcoes[0]?.valor ?? null;
  useEffect(() => {
    if (!aberto || (!sugerido && !padraoConvidado)) return;
    const dados = { ...(sugerido ? { papel_funcao: sugerido } : {}), ...(padraoConvidado ? { papel_id: padraoConvidado } : {}) };
    setAjuste((a) => ({ n: (a?.n ?? 0) + 1, dados }));
  }, [aberto, sugerido, padraoConvidado]);
  const campos: DefCampo[] = [
    {
      nome: 'tipo',
      rotulo: t('usuarios.convite.tipo'),
      tipo: 'selecao',
      obrigatorio: true,
      opcoes: tipos,
      desabilitado: !!funcionario,
      ajuda: motivoDoTipo ?? t(`usuarios.convite.tipoAjuda.${tipo}`),
    },
    // Funcionário
    {
      nome: 'origem',
      rotulo: t('usuarios.convite.origem'),
      tipo: 'selecao',
      obrigatorio: true,
      visivel: (v) => ehFuncionario(v) && !funcionario,
      opcoes: [
        { valor: 'existente', rotulo: t('usuarios.convite.origens.existente') },
        { valor: 'novo', rotulo: t('usuarios.convite.origens.novo') },
      ],
      ajuda: valores.origem === 'novo' ? t('usuarios.convite.novoAjuda') : undefined,
    },
    {
      nome: 'funcionario_id',
      rotulo: t('usuarios.convite.funcionario'),
      tipo: 'busca',
      obrigatorio: true,
      desabilitado: !!funcionario,
      visivel: ehExistente,
      ajuda: t('usuarios.convite.funcionarioAjuda'),
      busca: { caminho: '/pessoas/funcionarios', params: { sem_acesso: 'true' }, rotulo: (i) => String(i.nome), pessoa: true },
    },
    { nome: 'login_email', rotulo: t('usuarios.convite.entrarEmail'), tipo: 'booleano', largura: 'meia', visivel: (v) => !ehParceiro(v) },
    { nome: 'login_usuario', rotulo: t('usuarios.convite.entrarUsuario'), tipo: 'booleano', largura: 'meia', visivel: (v) => !ehParceiro(v) },
    // A explicação das duas formas fica num parágrafo só, depois das caixas (`depoisDoCampo`).
    { nome: 'nome', rotulo: t('usuarios.nome'), tipo: 'texto', obrigatorio: true, sensivel: 'nome', visivel: ehNovo },
    {
      nome: 'email',
      rotulo: t('usuarios.email'),
      tipo: 'email',
      // O funcionário já cadastrado usa o e-mail da ficha quando este fica vazio; o novo e o convidado só dispensam o e-mail
      // quando entram só pelo nome de usuário.
      obrigatorio: ehParceiro(valores) || (!ehExistente(valores) && comEmail(valores)),
      ajuda: ehParceiro(valores) ? undefined : ehExistente(valores) ? t('usuarios.convite.emailFichaAjuda') : comEmail(valores) ? undefined : t('usuarios.convite.emailOpcionalAjuda'),
    },
    {
      nome: 'usuario',
      rotulo: t('usuarios.convite.usuario'),
      tipo: 'texto',
      placeholder: exemploUsuario,
      ajuda: t('usuarios.convite.usuarioAjuda', { exemplo: exemploUsuario }),
      visivel: (v) => !ehParceiro(v) && comUsuario(v),
    },
    { nome: 'departamento_id', rotulo: tpe('campos.departamento'), tipo: 'busca', largura: 'meia', visivel: ehNovo, busca: { caminho: '/pessoas/departamentos', rotulo: (i) => String(i.nome) } },
    { nome: 'funcao_id', rotulo: tpe('campos.funcao'), tipo: 'busca', largura: 'meia', visivel: ehNovo, busca: { caminho: '/pessoas/funcoes', rotulo: (i) => String(i.nome) } },
    ...(empresas.length > 1
      ? ([
          {
            nome: 'empresa_id',
            rotulo: t('usuarios.convite.empresa'),
            tipo: 'selecao',
            visivel: ehNovo,
            opcoes: [{ valor: '', rotulo: t('usuarios.convite.empresaAtual') }, ...empresas.map((e) => ({ valor: e.id, rotulo: e.nome }))],
          },
        ] as DefCampo[])
      : []),
    { nome: 'papel_funcao', rotulo: t('usuarios.convite.papelFuncao'), tipo: 'selecao', obrigatorio: true, visivel: ehFuncionario, opcoes: opcoesFuncionario, ajuda: t('usuarios.convite.papelFuncaoAjuda') },
    // Cliente ou parceiro (Portal)
    { nome: 'parceiro_id', rotulo: t('usuarios.convite.parceiro'), tipo: 'busca', obrigatorio: true, visivel: ehParceiro, busca: { caminho: '/cadastros/parceiros', params: { operacao: true }, rotulo: (i) => String(i.nome) } },
    { nome: 'nome_portal', rotulo: t('usuarios.nome'), tipo: 'texto', sensivel: 'nome', visivel: ehParceiro },
    { nome: 'papel_portal', rotulo: t('usuarios.convite.papelPortal'), tipo: 'selecao', obrigatorio: true, visivel: ehParceiro, opcoes: PAPEIS_PORTAL.map((p) => ({ valor: p, rotulo: tp(`papel.${p}`) })) },
    // Convidado (o convite de antes)
    { nome: 'nome_convidado', rotulo: t('usuarios.nome'), tipo: 'texto', obrigatorio: soUsuarioSemEmail(valores), sensivel: 'nome', ajuda: soUsuarioSemEmail(valores) ? undefined : t('usuarios.convite.nomeAjuda'), visivel: ehConvidado },
    { nome: 'papel_id', rotulo: t('usuarios.papelUnico'), tipo: 'selecao', obrigatorio: true, visivel: ehConvidado, opcoes },
  ];
  const fechar = () => {
    setValores({});
    // O ajuste é da abertura: a próxima recomeça sem a sugestão da pessoa anterior.
    setAjuste(undefined);
    setIdAcesso(crypto.randomUUID());
    aoFechar();
  };
  return (
    <>
    <AcessoCriadoModal dados={criado} aoFechar={() => setCriado(null)} />
    <Modal
      aberto={aberto}
      aoFechar={fechar}
      titulo={t('usuarios.convite.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={fechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-convite" disabled={!!motivoDoTipo} icone={<Send className="size-4" />}>
            {soUsuarioSemEmail({ ...valores, tipo }) ? t('usuarios.convite.criarAcesso') : t('usuarios.convite.enviar')}
          </Botao>
        </>
      }
    >
      {aberto && (
        <Formulario
          id="form-convite"
          campos={campos}
          aoMudar={setValores}
          ajuste={ajuste}
          rotulosAtuais={funcionario ? { funcionario_id: funcionario.nome } : undefined}
          depoisDoCampo={tipo !== 'parceiro' ? { login_usuario: <p data-formas-convite="" className="text-xs text-tinta-3">{t('usuarios.convite.formasAjuda')}</p> } : undefined}
          inicial={{
            tipo: tipoInicial,
            login_email: true,
            login_usuario: true,
            origem: 'existente',
            papel_portal: 'dono',
            empresa_id: '',
            ...(funcionario ? { funcionario_id: funcionario.id } : {}),
            ...(padraoConvidado ? { papel_id: padraoConvidado } : {}),
          }}
          aoEnviar={async (v) => {
            const escolhido = (v.tipo as TipoConvite | undefined) ?? tipoInicial;
            const loginEmail = comEmail(v);
            const loginUsuario = comUsuario(v);
            if (escolhido !== 'parceiro' && !loginEmail && !loginUsuario) throw new Error(t('usuarios.convite.formaObrigatoria'));
            const usuario = loginUsuario ? String(v.usuario ?? '').trim() || undefined : undefined;
            const emailDigitado = String(v.email ?? '').trim() || undefined;
            const formas = { login_email: loginEmail, login_usuario: loginUsuario };
            const modoUsuario = escolhido !== 'parceiro' && soUsuarioSemEmail(v);
            let acesso: DadosAcessoCriado | null = null;
            if (escolhido === 'funcionario') {
              const papel = (v.papel_funcao as string | undefined) || undefined;
              if (v.origem === 'novo') {
                // Só nome de usuário e sem e-mail: a ficha nasce sem convite e o acesso é criado a seguir, ligado a ela.
                const r = (await post('/pessoas/funcionarios', {
                  nome: v.nome,
                  email: emailDigitado ?? null,
                  departamento_id: v.departamento_id || null,
                  funcao_id: v.funcao_id || null,
                  empresa_id: v.empresa_id || null,
                  papel_id: papel ?? null,
                  convidar: !modoUsuario,
                  ...(modoUsuario ? {} : { ...formas, usuario }),
                })) as { id: string };
                if (modoUsuario) acesso = await post<DadosAcessoCriado>(`/pessoas/funcionarios/${r.id}/convidar`, { papel_id: papel ?? null, modo: 'usuario', usuario });
              } else {
                // O e-mail vazio é o da ficha; a ficha sem e-mail com só o nome de usuário cai na senha provisória (o servidor decide).
                const r = await post<DadosAcessoCriado | { email: string | null; senha_provisoria?: string }>(`/pessoas/funcionarios/${String(v.funcionario_id)}/convidar`, {
                  papel_id: papel ?? null,
                  modo: 'email',
                  ...formas,
                  email_acesso: emailDigitado,
                  usuario,
                });
                if ('senha_provisoria' in r && r.senha_provisoria) acesso = r as DadosAcessoCriado;
              }
              await qc.invalidateQueries({ queryKey: ['/pessoas/funcionarios'] });
              await qc.invalidateQueries({ queryKey: ['pessoas'] });
              if (!acesso) toast.sucesso(t('usuarios.convite.enviado'));
            } else if (escolhido === 'parceiro') {
              await post(`/portal/parceiros/${String(v.parceiro_id)}/acessos`, {
                email: v.email,
                nome: v.nome_portal || undefined,
                papel: v.papel_portal,
                idioma: ['pt', 'en', 'es'].includes(i18n.language) ? i18n.language : 'pt',
              });
              await qc.invalidateQueries({ queryKey: ['portal'] });
              toast.sucesso(t('usuarios.convite.enviadoPortal'));
            } else if (modoUsuario) {
              acesso = await post<DadosAcessoCriado>('/admin/acessos', { id: idAcesso, nome: v.nome_convidado, usuario, papel_id: v.papel_id });
            } else {
              await post('/admin/convites', { email: emailDigitado, nome: v.nome_convidado || undefined, papel_id: v.papel_id, usuario, ...formas });
              toast.sucesso(t('usuarios.convite.enviado'));
            }
            await qc.invalidateQueries({ queryKey: ['/admin/convites'] });
            await qc.invalidateQueries({ queryKey: ['/admin/membros'] });
            fechar();
            if (acesso) setCriado(acesso);
          }}
        />
      )}
      {tipo === 'convidado' && <p className="mt-3 text-xs text-tinta-3">{t('usuarios.papelDonoAviso')}</p>}
    </Modal>
    </>
  );
}

/** Val6 (29/10): editar só o convite que quem vê pode conceder como está (a mesma régua do Reenviar). */
export const podeEditarConvite = (c: Pick<Convite, 'reenviavel'>) => podeReenviar(c);

/**
 * Val6 (29/10): o nome do convite abre a ficha (a da origem ou o cadastro pelo e-mail, quando quem vê a abre); o convidado
 * sem ficha (convite do Admin) abre o detalhe do convite, com "Criar ficha". O convite feito pela ficha que quem vê não abre
 * fica como texto.
 */
export const abreDetalheDoConvite = (s: Parameters<typeof caminhoDaFicha>[0], c: Pick<Convite, 'ficha' | 'origem'>) => !caminhoDaFicha(s, c.ficha) && !c.ficha && !c.origem;

/**
 * Val6 (29/10, ficha `FUN_editar-convite-e-abrir-ficha`): edita o convite pendente. Nome, papel, empresas e nome de usuário
 * mantêm o link e o prazo; outro e-mail cancela este convite e manda um novo ao endereço novo (o aviso diz isso). Aceito ou
 * encerrado enquanto a aba estava aberta: o erro (409) recarrega a lista e a linha some.
 */
export function EditarConviteModal({ convite, aoFechar }: { convite: Convite | null; aoFechar: () => void }) {
  const { t } = useTranslation('admin');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const papeis = usePapeis(!!convite);
  const [empresaIds, setEmpresaIds] = useState<Set<string>>(new Set());
  useEffect(() => setEmpresaIds(new Set(convite?.empresa_ids ?? [])), [convite]);
  const empresas = s.empresas?.opcoes ?? [];
  const opcoes = (papeis.data?.itens ?? []).filter((p) => p.codigo !== 'dono').map((p) => ({ valor: p.id, rotulo: nomeDoPapel(t, p) }));
  if (convite && !opcoes.some((o) => o.valor === convite.papel_id)) opcoes.unshift({ valor: convite.papel_id, rotulo: convite.papel_nome });
  const campos: DefCampo[] = [
    { nome: 'email', rotulo: t('usuarios.email'), tipo: 'email', obrigatorio: true, ajuda: t('usuarios.convite.emailTrocaAviso') },
    { nome: 'nome', rotulo: t('usuarios.nome'), tipo: 'texto', sensivel: 'nome', ajuda: t('usuarios.convite.nomeAjuda') },
    { nome: 'usuario', rotulo: t('usuarios.convite.usuario'), tipo: 'texto', placeholder: 'nome.sobrenome' },
    { nome: 'papel_id', rotulo: t('usuarios.papelUnico'), tipo: 'selecao', obrigatorio: true, opcoes },
  ];
  const fechar = () => aoFechar();
  return (
    <Modal
      aberto={!!convite}
      aoFechar={fechar}
      titulo={t('usuarios.convite.editarTitulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={fechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-editar-convite" icone={<Pencil className="size-4" />}>
            {t('usuarios.convite.salvar')}
          </Botao>
        </>
      }
    >
      {convite && (
        <>
          <Formulario
            id="form-editar-convite"
            campos={campos}
            inicial={{ email: convite.email, nome: convite.nome, usuario: convite.usuario ?? '', papel_id: convite.papel_id }}
            aoEnviar={async (v) => {
              try {
                const r = await api<{ id: string; novo_link: boolean }>('PUT', `/admin/convites/${convite.id}`, {
                  email: v.email,
                  nome: String(v.nome ?? ''),
                  papel_id: v.papel_id,
                  usuario: String(v.usuario ?? '').trim() || null,
                  ...(empresas.length > 1 ? { empresa_ids: [...empresaIds] } : convite.empresa_ids ? { empresa_ids: convite.empresa_ids } : {}),
                });
                toast.sucesso(r.novo_link ? t('usuarios.convite.editadoNovoLink') : t('usuarios.convite.editado'));
                await qc.invalidateQueries({ queryKey: ['/admin/convites'] });
                fechar();
              } catch (e) {
                if (conviteMorto(e)) {
                  await qc.invalidateQueries({ queryKey: ['/admin/convites'] });
                  toast.erro(mensagemDeErro(e));
                  fechar();
                  return;
                }
                throw e;
              }
            }}
          />
          {empresas.length > 1 && (
            <fieldset className="mt-4" data-empresas-convite="">
              <legend className="text-sm font-medium">{t('usuarios.convite.empresasConvite')}</legend>
              <p className="mb-2 text-xs text-tinta-3">{t('usuarios.convite.empresasConviteAjuda')}</p>
              <ul className="flex flex-col gap-1.5">
                {empresas.map((e) => (
                  <li key={e.id}>
                    <label className="flex cursor-pointer items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={empresaIds.has(e.id)}
                        onChange={() =>
                          setEmpresaIds((atual) => {
                            const n = new Set(atual);
                            if (n.has(e.id)) n.delete(e.id);
                            else n.add(e.id);
                            return n;
                          })
                        }
                      />
                      {e.nome}
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>
          )}
        </>
      )}
    </Modal>
  );
}

/**
 * Val6 (29/10): detalhe do convite do convidado sem ficha (decisão do coordenador, reversível): os dados do convite e "Criar
 * ficha", que cria a pessoa em Cadastros > Pessoas e empresas com o nome e o e-mail (o nome do convite passa a abrir a ficha pelo
 * e-mail) e a abre. Sem o Cadastros ou sem `cadastros.criar`, só o detalhe.
 */
export function DetalheConviteModal({ convite, aoFechar, aoEditar }: { convite: Convite | null; aoFechar: () => void; aoEditar?: (c: Convite) => void }) {
  const { t } = useTranslation('admin');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();
  const podeCriar = s.instalado('cadastros') && s.pode('cadastros.criar');
  const criar = useMutation({
    mutationFn: (c: Convite) => post<{ id: string }>('/cadastros/parceiros', { tipo: 'pessoa', nome: c.nome || c.email, email: c.email }),
    onSuccess: async (r) => {
      toast.sucesso(t('usuarios.convite.fichaCriada'));
      await qc.invalidateQueries({ queryKey: ['/admin/convites'] });
      aoFechar();
      navegar(`/cadastros/cadastros/parceiro/${r.id}`);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const nome = convite ? convite.nome || email(convite.email) : '';
  return (
    <Modal
      aberto={!!convite}
      aoFechar={aoFechar}
      titulo={t('usuarios.convite.detalheTitulo', { nome })}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.fechar')}
          </Botao>
          {convite && aoEditar && (
            <Botao icone={<Pencil className="size-4" />} onClick={() => aoEditar(convite)}>
              {t('usuarios.convite.editar')}
            </Botao>
          )}
          {convite && podeCriar && (
            <Botao variante="primario" icone={<UserPlus className="size-4" />} carregando={criar.isPending} onClick={() => criar.mutate(convite)}>
              {t('usuarios.convite.criarFicha')}
            </Botao>
          )}
        </>
      }
    >
      {convite && (
        <div data-detalhe-convite-corpo="" className="flex flex-col gap-3 text-sm">
          <dl className="flex flex-col gap-2">
            {[
              [t('usuarios.nome'), convite.nome || '-'],
              [t('usuarios.email'), email(convite.email)],
              [t('usuarios.papelUnico'), convite.papel_nome],
              [t('usuarios.estado'), convite.expirado ? t('usuarios.convite.expirado') : t('usuarios.convite.expiraEm', { quando: relativo(convite.expira_em) })],
            ].map(([rotulo, valor]) => (
              <div key={rotulo} className="min-w-0">
                <dt className="text-xs text-tinta-3">{rotulo}</dt>
                <dd className="break-words">{valor}</dd>
              </div>
            ))}
          </dl>
          {convite.convidado_por ? <p className="text-tinta-3">{t('usuarios.convite.convidadoPor', { nome: convite.convidado_por })}</p> : null}
          <p className="text-tinta-3">{t('usuarios.convite.detalheSemFicha')}</p>
          {podeCriar && <p className="text-xs text-tinta-3">{t('usuarios.convite.criarFichaAjuda')}</p>}
        </div>
      )}
    </Modal>
  );
}

/** O erro diz que o convite não está mais pendente (encerrado ou apagado): a linha dele é recarregada (rval1-14 VIS-B1). */
export function conviteMorto(e: unknown): boolean {
  // Review rval1-15 spec (B1): também o convite aceito enquanto a aba estava aberta (`convite.ja_aceito`).
  return e instanceof ErroRequisicao && ['convite.encerrado', 'convite.nao_encontrado', 'convite.ja_aceito'].includes(String(e.corpo.code));
}

/** Convites pendentes: editar (val6), reenviar (novo link e novo prazo) ou cancelar. O nome abre a ficha (val6). */
export function Convites() {
  const { t } = useTranslation('admin');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [cancelar, setCancelar] = useState<Convite | null>(null);
  const [editar, setEditar] = useState<Convite | null>(null);
  const [detalhe, setDetalhe] = useState<Convite | null>(null);
  const q = useQuery({ queryKey: ['/admin/convites'], queryFn: () => get<Lista<Convite>>('/admin/convites') });
  const papeisLista = usePapeis();
  const papelDoConvite = (c: Convite) => {
    const p = papeisLista.data?.itens.find((x) => x.id === c.papel_id);
    return { nome: c.papel_nome, codigo: p?.codigo, de_fabrica: p?.de_fabrica };
  };
  const podeEditar = s.pode('admin.usuarios');
  const recarregar = () => qc.invalidateQueries({ queryKey: ['/admin/convites'] });
  // Review rval1-14 visual (B1): o convite encerrado ou apagado por fora (`convite.encerrado`, `convite.nao_encontrado`) sai da
  // lista na hora; antes a linha morta seguia com Reenviar e Cancelar até recarregar a página.
  const recarregarSeMorto = (e: unknown) => {
    if (conviteMorto(e)) void recarregar();
  };
  const reenviar = useMutation({
    mutationFn: (c: Convite) => post(`/admin/convites/${c.id}/reenviar`),
    onSuccess: async () => {
      toast.sucesso(t('usuarios.convite.reenviado'));
      await recarregar();
    },
    onError: (e) => {
      toast.erro(mensagemDeErro(e));
      recarregarSeMorto(e);
    },
  });
  const remover = useMutation({
    mutationFn: (c: Convite) => del(`/admin/convites/${c.id}`),
    onSuccess: async () => {
      setCancelar(null);
      toast.sucesso(t('usuarios.convite.cancelado'));
      await recarregar();
    },
    onError: (e) => {
      setCancelar(null);
      toast.erro(mensagemDeErro(e));
      recarregarSeMorto(e);
    },
  });
  return (
    <>
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <Vazio icone={<Mail className="size-5" aria-hidden />} texto={t('usuarios.convite.vazio')} />
          ) : (
            <ul className="divide-y divide-borda rounded-xl border border-borda bg-sup-1">
              {d.itens.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      {c.nome ? (
                        <span className="truncate" title={email(c.email)}>{email(c.email)}</span>
                      ) : (
                        <NomeComFicha s={s} ficha={c.ficha} className="truncate" title={email(c.email)} rotulo={t('usuarios.convite.abrirFicha', { nome: email(c.email) })} rotuloDetalhe={t('usuarios.convite.verConvite', { nome: email(c.email) })} aoAbrirDetalhe={abreDetalheDoConvite(s, c) ? () => setDetalhe(c) : undefined}>
                          {email(c.email)}
                        </NomeComFicha>
                      )}
                      <Selo cor="azul">{nomeDoPapel(t, papelDoConvite(c))}</Selo>
                      {c.expirado ? <Selo cor="laranja">{t('usuarios.convite.expirado')}</Selo> : null}
                    </p>
                    <p className="mt-0.5 text-xs text-tinta-3">
                      {c.nome ? (
                        <>
                          <NomeComFicha s={s} ficha={c.ficha} rotulo={t('usuarios.convite.abrirFicha', { nome: c.nome })} rotuloDetalhe={t('usuarios.convite.verConvite', { nome: c.nome })} aoAbrirDetalhe={abreDetalheDoConvite(s, c) ? () => setDetalhe(c) : undefined}>
                            {c.nome}
                          </NomeComFicha>
                          {' · '}
                        </>
                      ) : (
                        ''
                      )}
                      {c.expirado ? t('usuarios.convite.expirado') : t('usuarios.convite.expiraEm', { quando: relativo(c.expira_em) })}
                      {c.convidado_por ? ` · ${t('usuarios.convite.convidadoPor', { nome: c.convidado_por })}` : ''}
                    </p>
                  </div>
                  {podeEditar && (
                    <div className="flex flex-wrap gap-2">
                      {podeEditarConvite(c) && (
                        <Botao tamanho="sm" icone={<Pencil className="size-4" />} onClick={() => setEditar(c)}>
                          {t('usuarios.convite.editar')}
                        </Botao>
                      )}
                      {podeReenviar(c) && (
                        <Botao tamanho="sm" icone={<Send className="size-4" />} carregando={reenviar.isPending && reenviar.variables?.id === c.id} onClick={() => reenviar.mutate(c)}>
                          {t('usuarios.convite.reenviar')}
                        </Botao>
                      )}
                      <Botao tamanho="sm" variante="perigo" icone={<X className="size-4" />} onClick={() => setCancelar(c)}>
                        {t('usuarios.convite.cancelar')}
                      </Botao>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )
        }
      </Estado>
      <Confirmar
        aberto={!!cancelar}
        aoFechar={() => setCancelar(null)}
        aoConfirmar={() => cancelar && remover.mutate(cancelar)}
        carregando={remover.isPending}
        perigoso
        titulo={t('usuarios.convite.cancelarTitulo', { email: email(cancelar?.email) })}
        texto={t('usuarios.convite.cancelarTexto')}
      />
      <EditarConviteModal convite={editar} aoFechar={() => setEditar(null)} />
      <DetalheConviteModal
        convite={detalhe}
        aoFechar={() => setDetalhe(null)}
        aoEditar={
          podeEditar && detalhe && podeEditarConvite(detalhe)
            ? (c) => {
                setDetalhe(null);
                setEditar(c);
              }
            : undefined
        }
      />
    </>
  );
}
